import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TextField, Drawer, Box, Button, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useReminderAnalyses from 'hooks/useReminderAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { reminderAnalysesSelectors } from 'features/reminderAnalyses';

const ReminderAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { reminder_analysis_id } = useParams();
  const { getReminderAnalysis, clearLocalActiveReminderAnalysis } = useReminderAnalyses();
  const { getReminderAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const reminderAnalysisFetching = useCustomSelector(reminderAnalysesSelectors.reminderAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchReminderAnalysis = useCallback((id) => {
    setOpen(true);
    getReminderAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { reminder_analysis } } = payload;
        handleSetData(reminder_analysis);
      }
    })
  }, [getReminderAnalysis]);

  const handleSetData = (reminder_analysis) => {
    setResult(reminder_analysis?.result);
  };

  const handleExiting = () => {
    setResult('');
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl93t`);
    clearLocalActiveReminderAnalysis();
    clearLocalAnalysesEvents();

  };

  const handleClose = () => setOpen(false);

  const fetchReminderAnalysesEvents = useCallback((offset, reminder_analysis_id) => {
    getReminderAnalysesEvents({ offset, reminder_analysis_id })
  }, [ getReminderAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchReminderAnalysesEvents(0, reminder_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!reminder_analysis_id) fetchReminderAnalysis(reminder_analysis_id);
  }, [reminder_analysis_id, fetchReminderAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { reminderAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {reminder_analysis_id}</Typography>
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={reminder_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchReminderAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <ReminderAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);